import { useQueryClient } from '@tanstack/react-query'
import { useMatches } from '@tanstack/react-router'
import type { CrumbMeta, StaticCrumb } from '@/bootstrap/typings'
import { getEntityRecordLabel } from '@/lib/entity-route-i18n'

type EntityListQueryResult = {
	data?: Array<Record<string, unknown>>
	total?: number
}

function isEntityListQueryResult(
	value: unknown,
): value is EntityListQueryResult {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return false
	}

	const candidate = value as Record<string, unknown>
	if (!('data' in candidate) || !Array.isArray(candidate.data)) {
		return false
	}

	if ('total' in candidate && typeof candidate.total !== 'number') {
		return false
	}

	return true
}

export function useBreadcrumbs() {
	const matches = useMatches()
	const queryClient = useQueryClient()
	const isEntityDetailMatch = (match: (typeof matches)[number]) =>
		typeof match.id === 'string' &&
		match.id.includes('$entityId') &&
		!match.id.includes('$tab') &&
		!match.id.includes('$section') &&
		typeof match.params?.entity === 'string' &&
		typeof match.params?.entityId === 'string'

	const entityDetailMatch = matches.find((match) => isEntityDetailMatch(match))
	const entity = String(entityDetailMatch?.params?.entity ?? '')
	const entityId = String(entityDetailMatch?.params?.entityId ?? '')
	const crumbLabelFromSearch = matches
		.map((match) => {
			const search = match.search
			if (
				typeof search === 'object' &&
				search &&
				!Array.isArray(search) &&
				typeof search.crumbLabel === 'string'
			) {
				return String(search.crumbLabel)
			}

			return undefined
		})
		.find((label): label is string => typeof label === 'string')

	const entityListSnapshots = entity
		? queryClient.getQueriesData<EntityListQueryResult>({
				queryKey: [entity],
			})
		: []

	const entityRecord = entityListSnapshots
		.filter(([queryKey, queryData]) => {
			if (!Array.isArray(queryKey) || queryKey[0] !== entity) {
				return false
			}

			// Keep only list queries (entity + params), avoid mapping/detail/settings entries.
			if (queryKey.length !== 2) {
				return false
			}

			return isEntityListQueryResult(queryData)
		})
		.flatMap(([, queryData]) => queryData?.data ?? [])
		.find((item) => {
			if (!item || typeof item !== 'object' || Array.isArray(item)) {
				return false
			}

			const id = item.id
			return typeof id === 'string' && id === entityId
		})

	const entityRecordLabel =
		crumbLabelFromSearch ||
		(entityRecord
			? getEntityRecordLabel(entity, entityRecord, entityId)
			: undefined)

	const withEntityLabelOverride = (
		match: (typeof matches)[number],
		crumb: CrumbMeta,
	): { match: (typeof matches)[number]; crumb: CrumbMeta } => {
		if (isEntityDetailMatch(match) && entityRecordLabel) {
			return {
				match,
				crumb: {
					...crumb,
					label: entityRecordLabel,
				},
			}
		}

		return { match, crumb }
	}

	const crumbs = matches
		.map((m) => {
			const fromLoader: CrumbMeta | undefined = m.loaderData?.breadcrumb
			if (fromLoader) {
				return withEntityLabelOverride(m, fromLoader)
			}

			const fromStatic: StaticCrumb | undefined = m.staticData?.breadcrumb
			if (!fromStatic) return undefined

			const crumb =
				typeof fromStatic === 'function'
					? fromStatic({ params: m.params, search: m.search })
					: fromStatic

			return withEntityLabelOverride(m, crumb)
		})
		.filter(
			(value): value is { match: (typeof matches)[number]; crumb: CrumbMeta } =>
				value !== undefined,
		)

	return crumbs
}
